import React from "react";
import { logError } from "@/lib/errorLogger";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Props {
  children: React.ReactNode;
  /** Called on retry — host can re-resolve selectors / identity. */
  onRetry?: () => void;
}

interface State {
  hasError: boolean;
  message: string;
}

export class ContractorPortalErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false, message: "" };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, message: error.message };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    logError({
      message: error.message,
      stack: error.stack,
      module: "ContractorPortal",
      severity: "error",
      context: { componentStack: info.componentStack },
    });
  }

  handleRetry = () => {
    this.setState({ hasError: false, message: "" });
    this.props.onRetry?.();
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div
        data-testid="contractor-portal-error-boundary"
        className="min-h-screen flex items-center justify-center bg-background p-6"
      >
        <div className="max-w-sm w-full rounded-xl border border-border bg-card p-6 text-center">
          <AlertTriangle className="mx-auto mb-3 h-10 w-10 text-destructive" />
          <h1 className="text-base font-semibold mb-1">Something went wrong loading your dashboard.</h1>
          <p className="text-xs text-muted-foreground mb-4">Tap to retry.</p>
          {import.meta.env.DEV && (
            <pre className="mb-3 max-h-24 overflow-auto rounded bg-muted p-2 text-left text-[10px] text-muted-foreground">
              {this.state.message}
            </pre>
          )}
          <Button onClick={this.handleRetry} className="w-full">
            <RefreshCw className="mr-2 h-4 w-4" /> Retry
          </Button>
        </div>
      </div>
    );
  }
}
